import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Отрасль больше не спрашиваем при регистрации грузовладельца, но бэкенд её требует —
 * отправляем «Оптовая торговля и дистрибуция» (id 24 в справочнике /dic/industries).
 */
export const DEFAULT_INDUSTRY_ID = 24;

export interface CargoOwnerRegistrationRequest {
  email: string;
  fullName: string;
  phone?: string;
  password: string;
  companyName: string;
  industryId: number;
  countryId: number;
}

export interface FreightForwarderRegistrationRequest {
  email: string;
  fullName: string;
  phone?: string;
  password: string;
  companyName: string;
  countryId: number;
}

export interface RegistrationRequest {
  email: string;
  fullName: string;
  phone?: string;
  password: string;
  roleId?: number;
}

export interface UpgradeToCargoOwnerRequest {
  email: string;
  password: string;
  fullName?: string;
  phone?: string;
  companyName: string;
  industryId: number;
  countryId: number;
}

/**
 * Регистрация поставщика. token задан — переход по ссылке-приглашению (вкладка
 * «Поставка»), поставщик привяжется к этой перевозке; не задан — самостоятельная
 * регистрация (главная страница, "Я - поставщик"), без привязки.
 */
export interface SupplierRegistrationRequest {
  token?: string;
  email: string;
  fullName: string;
  phone?: string;
  password: string;
  companyName: string;
  countryId: number;
}

/** Публичный (без авторизации) предпросмотр приглашения поставщика по токену. */
export interface SupplierInvitePreview {
  valid: boolean;
  alreadyUsed: boolean;
  calculationTitle?: string;
  fromCountryName?: string;
  fromCity?: string;
  toCountryName?: string;
  toCity?: string;
}

export interface UserDto {
  id: number;
  email: string;
  fullName: string;
  phone?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  private apiUrl = '/cargo/registration';

  constructor(private http: HttpClient) {}

  phoneExists(phone: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/phone-exists`, { params: { phone } });
  }

  register(request: RegistrationRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.apiUrl}`, request);
  }

  registerCargoOwner(request: CargoOwnerRegistrationRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.apiUrl}/cargo-owner`, request);
  }

  registerFreightForwarder(request: FreightForwarderRegistrationRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.apiUrl}/freight-forwarder`, request);
  }

  /** Апгрейд текущего (публичного) пользователя до грузовладельца — тот же аккаунт. */
  upgradeToCargoOwner(request: UpgradeToCargoOwnerRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.apiUrl}/upgrade-to-cargo-owner`, request);
  }

  /** Регистрация поставщика по ссылке-приглашению. */
  registerSupplier(request: SupplierRegistrationRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.apiUrl}/supplier`, request);
  }

  /** Публичный предпросмотр приглашения поставщика (без авторизации). */
  getSupplierInvitePreview(token: string): Observable<SupplierInvitePreview> {
    return this.http.get<SupplierInvitePreview>(`/cargo/open-api/supplier-invites/${token}`);
  }
}
